import { ReactComponent as MermaidIcon } from 'assets/icons/logos/mermaid24.svg'
import cx from 'classnames'
import { CommentInteractionButton, HyperlinkInteractionButton } from 'common/InteractionButton'
import { TaskInteractionButton } from 'common/InteractionButton/components/TaskInteractionButton/TaskInteractionButton'
import { useReactNodeView } from 'common/Prose/nodeViews/ReactNodeView/ReactNodeViewContext'
import { useModalStore } from 'hooks/stores/useModalStore'
import mermaid from 'mermaid'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ResizableBox, ResizeCallbackData, ResizeHandle } from 'react-resizable'
import { useRootStore } from 'stores/RootStore'
import { resizeHandleToCursor } from 'utils/resizable'
import { EditMermaidModal, EditMermaidModalCloseIcon } from '../EditMermaidModal/EditMermaidModal'
import { MermaidInlineFormatBar } from '../MermaidInlineFormatBar/MermaidInlineFormatBar'
import Styles from './MermaidComponent.module.scss'

const MIN_WIDTH = 50
const DEFAULT_WIDTH = 300

mermaid.initialize({ startOnLoad: true })

export const MermaidComponent = React.forwardRef<HTMLDivElement, any>(({ children }, contentDomRef) => {
  const { nodeView, node, view, getPos, inCommentMode } = useReactNodeView()
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const inputContainerRef = useRef<HTMLDivElement>(null)
  const mermaidRef = useRef<HTMLDivElement>(null)
  const sizeContainerRef = useRef<HTMLDivElement>(null)
  const [addingCaption, setAddingCaption] = useState(false)
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle | null>(null)
  const [selected, setSelected] = useState(false)
  const calculatedWidth = node?.attrs.width && node.attrs.width > MIN_WIDTH ? node.attrs.width : DEFAULT_WIDTH
  const [height, setHeight] = useState(calculatedWidth) // Use width as height for square aspect ratio
  const [width, setWidth] = useState(calculatedWidth)
  const [caption, setCaption] = useState(node?.attrs.description || '')
  const { openModal, closeModal } = useModalStore()
  const rootStore = useRootStore()
  const docStore = rootStore.docStore
  const containerWidth = sizeContainerRef.current?.parentElement?.offsetWidth

  const comments = node?.attrs.uuids ?? []
  const hasComments = comments.length > 0
  const tasks = node?.attrs.taskUuids ?? []
  const hasTasks = tasks.length > 0
  const float = node?.attrs.float
  const readOnly = !view?.editable
  const code = node?.attrs.code

  useEffect(() => {
    if (nodeView) {
      nodeView.selectNode = () => setSelected(true)
      nodeView.deselectNode = () => setSelected(false)
      nodeView.ignoreMutation = () => true
      nodeView.stopEvent = (e) => {
        if (e.target) {
          if ((e.target as HTMLElement).classList.contains(Styles.resizeHandle)) {
            e.preventDefault()

            return true
          }

          if ((e.target as HTMLElement).nodeName === 'TEXTAREA' || (e.target as HTMLElement).nodeName === 'INPUT') {
            setSelected(true)
            return true
          }
        }

        if (e.type.includes('drag')) return false

        return false
      }
    }
  }, [nodeView])

  useLayoutEffect(() => {
    delete mermaidRef.current?.dataset.processed

    setTimeout(() => {
      if (mermaidRef.current && code) {
        mermaid.run()
      }
    })
  }, [code])

  const containerClass = cx({
    [Styles.alignLeft]: float === 'left',
    [Styles.alignRight]: float === 'right',
    [Styles.hasComments]: hasComments,
    [Styles.readOnly]: readOnly,
  })

  const updateAttributes = (updatedAttrs: Record<string, any>) => {
    if (!view || !getPos) return

    const newAttrs = { ...node?.attrs, ...updatedAttrs }
    const tx = view.state.tr.setNodeMarkup(getPos(), undefined, newAttrs)
    view.dispatch(tx)
  }

  const openCodeModal = () => {
    openModal(EditMermaidModal, {
      modalProps: {
        code,
        updateAttributes,
        closeModal,
      },
      modalOptions: {
        title: 'Mermaid.js Diagram',
        width: 1000,
        closeIcon: <EditMermaidModalCloseIcon />,
      },
    })
  }

  const getResizeHandle = (position: ResizeHandle) => {
    return (
      <span
        className={cx(
          Styles.resizeHandle,
          Styles[`resizeHandle-${position}`],
          { [Styles.borderRed]: width <= MIN_WIDTH },
          { [Styles.hide]: !selected || readOnly },
        )}
        data-test-id={`image-resize-handle-${position}`}
      />
    )
  }

  return (
    <div ref={sizeContainerRef}>
      <div
        ref={contentDomRef}
        className={cx(Styles.container, containerClass, Styles.hoverContainer)}
        onDoubleClick={openCodeModal}
      >
        {resizeHandle && (
          <style>{`
        body {
          cursor: ${resizeHandleToCursor(resizeHandle)};
        }
        `}</style>
        )}
        {view && docStore && (!readOnly || inCommentMode) && selected && (
          <MermaidInlineFormatBar
            view={view}
            nodePos={getPos()}
            docStore={docStore}
            mermaidRef={mermaidRef}
            isUnset={!code}
            editCaption={() => {
              setAddingCaption(true)
              setTimeout(() => inputRef.current?.focus(), 0)
            }}
          />
        )}
        {code ? (
          <div className={Styles.interactionsAnchor}>
            <div className={Styles.interactionsContainer}>
              {node?.attrs.href && (
                <HyperlinkInteractionButton
                  aria-label="Hyperlink interaction button for an image."
                  href={node.attrs.href}
                />
              )}
              {docStore && hasComments && (!readOnly || inCommentMode) && (
                <mark data-uuid={comments.join(' ')}>
                  <CommentInteractionButton
                    aria-label="A button that displays the comments left on the diagram."
                    uuids={comments}
                    docStore={docStore}
                  />
                </mark>
              )}

              {docStore && hasTasks && !readOnly && (
                <mark data-uuid={tasks.join(' ')}>
                  <TaskInteractionButton
                    aria-label="A button that displays the tasks left on the diagram."
                    uuids={tasks}
                    docStore={docStore}
                  />
                </mark>
              )}
            </div>
            <ResizableBox
              className={Styles.resizeContainer}
              onResizeStop={(_event, { size: { width } }) => {
                updateAttributes({ width: width })
                setResizeHandle(null)
              }}
              height={height}
              width={width}
              lockAspectRatio={true}
              resizeHandles={['nw', 'ne', 'se', 'sw']}
              maxConstraints={containerWidth ? [containerWidth, containerWidth] : undefined}
              handle={getResizeHandle}
              onResize={(_event, { size }: ResizeCallbackData) => {
                setWidth(size.width)
                setHeight(size.height)
              }}
              onResizeStart={(_event, { handle }: ResizeCallbackData) => {
                setResizeHandle(handle)
              }}
            >
              <div
                ref={mermaidRef}
                className={cx(Styles.preview, 'mermaid', { [Styles.previewSelected]: selected && !readOnly })}
              >
                {code}
              </div>
            </ResizableBox>

            <div
              className={cx(Styles.captionContainer, {
                [Styles.captionContainerFilled]: !!node?.attrs.description || addingCaption,
              })}
              ref={inputContainerRef}
            >
              <textarea
                id={`mermaid-caption-${node.attrs.blockId}}`}
                autoFocus={addingCaption}
                className={cx(Styles.bottomInput)}
                data-test-id="mermaid-caption"
                disabled={readOnly || !!inCommentMode}
                onBlur={() => {
                  setAddingCaption(false)
                  updateAttributes({ description: caption })
                }}
                onChange={(e) => setCaption(e.target.value)}
                onFocus={() => {
                  setAddingCaption(true)
                }}
                placeholder="Caption here"
                ref={inputRef}
                value={caption}
              />
            </div>
          </div>
        ) : (
          <div
            ref={mermaidRef}
            className={cx(Styles.empty, { [Styles.selected]: selected })}
            onDoubleClick={openCodeModal}
          >
            <MermaidIcon />
            <div>Double click to start diagramming on Mermaid.js!</div>
          </div>
        )}
      </div>
    </div>
  )
})
